<!--
  AddModelDialog — the simple-mode way to add a new model (a real ModelConfig) without leaving the
  Assistants editor. It is a thin wrapper around the SAME legacy create flow the Advanced Models page
  uses: ModelCard in create mode (no modelId) inside a ConfigCardOverlay, exactly as Models.vue mounts
  it for "New Model". No form logic lives here — provider list, model autocomplete/sync, options and
  the save itself are all ModelCard's.

  On save it resolves the new model's id (ModelCard emits the name; setModelConfig has already
  refreshed the store by then) and emits it so the picker can select it immediately.
-->
<template>
  <ConfigCardOverlay
    :model-value="modelValue"
    title="Add a new model"
    @update:model-value="onToggle"
    @close="onToggle(false)"
  >
    <v-alert
      v-if="!connected.size"
      type="info"
      variant="tonal"
      density="comfortable"
      icon="mdi-power-plug-outline"
      class="mb-0"
    >
      Connect a provider on the <strong>Providers</strong> tab first, then add a model here.
    </v-alert>

    <!-- v-if remounts a fresh create card every open, so a cancelled entry never lingers. -->
    <ModelCard v-else-if="modelValue" isOverlay @saved="onSaved" @remove-card="onToggle(false)" />
  </ConfigCardOverlay>
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component'
import { mapGetters } from 'vuex'
import ModelCard from '@/components/cards/ModelCard.vue'
import ConfigCardOverlay from '@/components/overlays/ConfigCardOverlay.vue'
import type { ModelConfig } from '@/types/models'

@Options({
  components: { ModelCard, ConfigCardOverlay },
  props: {
    modelValue: { type: Boolean, default: false },
    connected: { type: Object, required: true } // Set<string> of connected provider keys
  },
  emits: ['update:modelValue', 'created'],
  computed: {
    ...mapGetters(['modelConfigsList'])
  }
})
export default class AddModelDialog extends Vue {
  modelValue!: boolean
  connected!: Set<string>
  modelConfigsList!: ModelConfig[]

  onToggle(open: boolean) {
    if (!open) this.$emit('update:modelValue', false)
  }

  // ModelCard emits 'saved' after setModelConfig has refreshed the store, so the new config is
  // already in modelConfigsList. Names are unique (the store's configs map is keyed by name).
  onSaved(payload?: { name?: string }) {
    const created = payload?.name ? (this.modelConfigsList || []).find((m) => m.name === payload.name) : undefined
    if (created) this.$emit('created', created.id)
    this.$emit('update:modelValue', false)
  }
}
</script>
